import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Terminal, Loader2, HelpCircle } from 'lucide-react';
import { cn, API_BASE_URL, SOCKET_URL } from '../../lib/utils';
import { io, Socket } from 'socket.io-client';
import { useAppSelector } from '../../store';
import { useActiveEvent } from '../../contexts/EventContext';
import {
  parseCommandAction,
  looksLikeCommandAction,
  findResourceByQuery,
  findIncidentByQuery,
  ACTION_BAR_SHORT_CODES,
  type ResourceHistoryIncident,
} from '../../lib/commandActions';
import { dispatchMapZoomToResource } from '../../lib/mapCommandEvents';
import { FOCUS_ACTION_LINE_EVENT } from '../../lib/actionLineFocus';
import { submitOrQueue } from '../../lib/resilientApi';
import type { Incident, Resource, EventStatus, C2Event } from '../../types';
import ResourceAssignedListModal from './ResourceAssignedListModal';

interface CommandActionBarProps {
  incidents: Incident[];
  onOpenIncident: (incidentId: string) => void;
  /** Plain-text filter when input is not a short code */
  onSearchChange: (query: string) => void;
  searchQuery: string;
  className?: string;
  inputClassName?: string;
}

interface FeedbackState {
  type: 'success' | 'error' | 'info';
  message: string;
}

export default function CommandActionBar({
  incidents,
  onOpenIncident,
  onSearchChange,
  searchQuery,
  className,
  inputClassName,
}: CommandActionBarProps) {
  const { token } = useAppSelector((state) => state.auth);
  const { activeEventId, activeEvent, setActiveEventLocal } = useActiveEvent();
  const location = useLocation();
  const [running, setRunning] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const [alModal, setAlModal] = useState<{
    resourceLabel: string;
    incidents: ResourceHistoryIncident[];
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  const focusInput = useCallback(() => {
    window.requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
  }, []);

  useEffect(() => {
    if (location.pathname === '/workspace/incidents') {
      focusInput();
    }
  }, [location.pathname, focusInput]);

  useEffect(() => {
    const handler = () => focusInput();
    window.addEventListener(FOCUS_ACTION_LINE_EVENT, handler);
    return () => window.removeEventListener(FOCUS_ACTION_LINE_EVENT, handler);
  }, [focusInput]);

  useEffect(() => {
    if (!showHelp) return;
    const onPointerDown = (e: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) {
        setShowHelp(false);
        focusInput();
      }
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [showHelp, focusInput]);

  useEffect(() => {
    if (!feedback) return;
    const t = window.setTimeout(() => setFeedback(null), 4500);
    return () => window.clearTimeout(t);
  }, [feedback]);

  useEffect(() => {
    if (!token) return;
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      auth: { token },
    });
    socketRef.current = socket;
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token]);

  const showFeedback = (type: FeedbackState['type'], message: string) => {
    setFeedback({ type, message });
  };

  const fetchResources = useCallback(async (): Promise<Resource[]> => {
    const url = activeEventId
      ? `${API_BASE_URL}/resources?eventId=${encodeURIComponent(activeEventId)}`
      : `${API_BASE_URL}/resources`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Failed to load resources');
    const data = await res.json();
    return data.resources || [];
  }, [activeEventId, token]);

  const resolveResource = async (query: string): Promise<Resource> => {
    const resources = await fetchResources();
    const match = findResourceByQuery(resources, query);
    if (!match) throw new Error(`Resource "${query}" not found`);
    return match;
  };

  const runLastAssigned = async (resourceQuery: string) => {
    const resource = await resolveResource(resourceQuery);
    if (resource.assignedIncident) {
      onOpenIncident(resource.assignedIncident);
      showFeedback('success', `Opened current assignment for ${resource.name}`);
      return;
    }

    const res = await fetch(`${API_BASE_URL}/resources/${encodeURIComponent(resource.id)}/history`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to load resource history');
    const data = await res.json();
    const list = (data.incidents || []) as ResourceHistoryIncident[];
    if (list.length === 0) throw new Error(`No incidents found for ${resource.name}`);

    onOpenIncident(list[0].id);
    showFeedback('success', `Opened last incident for ${resource.name}`);
  };

  const runAssignedList = async (resourceQuery: string) => {
    const resource = await resolveResource(resourceQuery);
    const res = await fetch(`${API_BASE_URL}/resources/${encodeURIComponent(resource.id)}/history`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to load resource history');
    const data = await res.json();
    const list = ((data.incidents || []) as ResourceHistoryIncident[]).map((inc) => {
      const full = incidents.find((i) => i.id === inc.id);
      return {
        ...inc,
        location: full?.location?.address || undefined,
      };
    });
    setAlModal({ resourceLabel: resource.name, incidents: list });
  };

  const runZoomToResource = async (resourceQuery: string) => {
    const resource = await resolveResource(resourceQuery);
    const res = await fetch(`${API_BASE_URL}/tracking`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to load tracking');
    const data = await res.json();
    const positions = data.positions || [];
    const pos = positions.find(
      (p: { resourceId?: string; resourceName?: string }) =>
        p.resourceId === resource.id ||
        String(p.resourceName || '').toLowerCase() === resource.name.toLowerCase()
    );

    const offline = !pos || !pos.latitude || !pos.longitude || !!pos.disconnectedAt;

    dispatchMapZoomToResource({
      resourceId: resource.id,
      resourceName: resource.name,
      latitude: pos?.latitude,
      longitude: pos?.longitude,
      offline,
      status: pos?.status,
    });

    if (offline) {
      showFeedback('error', `${resource.name} tracking offline — no live position`);
    } else {
      showFeedback('success', `Map centred on ${resource.name}`);
    }
  };

  const runOpenIncident = (incidentRef: string) => {
    const match = findIncidentByQuery(incidents, incidentRef);
    if (!match) throw new Error(`Incident "${incidentRef}" not found`);
    onOpenIncident(match.id);
    showFeedback('success', `Opened ${match.id}`);
  };

  const runSetEventStatus = async (status: EventStatus, reason: string) => {
    if (!activeEvent) {
      throw new Error('No active event — open an event workspace first');
    }
    if (status === activeEvent.status) {
      throw new Error(`Event is already ${status}`);
    }

    const noteText = reason.trim();
    if (status === 'supervision' && !noteText) {
      throw new Error('Supervision reason required — STATUS/supervision/[reason]');
    }
    if (status === 'closed' && !noteText) {
      throw new Error('Close reason required — STATUS/closed/[reason]');
    }
    if (status === 'open' && activeEvent.status !== 'open' && !noteText) {
      throw new Error('Reopen reason required — STATUS/open/[reason]');
    }

    if (status === 'closed') {
      const stillOpen = incidents.filter((inc) => inc.status !== 'closed');
      if (stillOpen.length > 0) {
        throw new Error(`Cannot close event — ${stillOpen.length} incident(s) still open`);
      }
    }

    const applyLocalEventStatus = () => {
      const updated: C2Event = {
        ...activeEvent,
        status,
        statusNote: noteText || undefined,
        statusChangedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      if (status === 'closed') updated.closedAt = new Date().toISOString();
      if (status === 'open') updated.closedAt = undefined;
      setActiveEventLocal(updated);
    };

    const body = { status, note: noteText || undefined };

    const queueAndApply = async () => {
      const result = await submitOrQueue({
        method: 'POST',
        path: `/events/${activeEvent.id}/set-status`,
        body,
        token,
      });
      if (!result.ok && !result.queued) {
        throw new Error('Failed to update event status');
      }
      applyLocalEventStatus();
      showFeedback(
        'success',
        `Event → ${status.replace('_', ' ')}${result.queued ? ' (queued)' : ''}`
      );
    };

    try {
      const res = await fetch(`${API_BASE_URL}/events/${activeEvent.id}/set-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setActiveEventLocal(data.event);
        showFeedback('success', `Event → ${status.replace('_', ' ')}`);
        return;
      }
      if (res.status >= 500) {
        await queueAndApply();
        return;
      }
      throw new Error(data.error || 'Failed to update event status');
    } catch (err) {
      const transient =
        err instanceof TypeError ||
        (err instanceof Error && err.message === 'Server error');
      if (!transient) {
        throw err instanceof Error ? err : new Error('Failed to update event status');
      }
      await queueAndApply();
    }
  };

  const runSendMessage = async (resourceQuery: string, text: string) => {
    const resource = await resolveResource(resourceQuery);
    const channelsRes = await fetch(
      `${API_BASE_URL}/chat/channels${activeEventId ? `?eventId=${encodeURIComponent(activeEventId)}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!channelsRes.ok) throw new Error('Failed to load message channels');
    const channelsData = await channelsRes.json();
    const channels = channelsData.channels || [];

    const channel = channels.find(
      (ch: { resourceId?: string; resourceName?: string; type: string; targetUserId?: string }) =>
        ch.type === 'private' &&
        (ch.resourceId === resource.id ||
          String(ch.resourceName || '').toLowerCase() === resource.name.toLowerCase())
    );

    let targetUserId = channel?.targetUserId || resource.inUseBy || null;

    if (!targetUserId) {
      throw new Error(`${resource.name} is not on MDT — no one to message`);
    }

    const payload = {
      channelType: 'private',
      targetUserId,
      text,
      eventId: activeEventId || undefined,
    };

    if (socketRef.current?.connected) {
      socketRef.current.emit('chat:message', payload);
    } else {
      const result = await submitOrQueue({
        method: 'POST',
        path: '/chat/messages',
        body: payload,
        token,
        queueType: 'chat_message',
        queuePayload: {
          channelType: 'private',
          targetUserId,
          text,
          eventId: activeEventId || undefined,
        },
      });
      if (!result.ok && !result.queued) {
        throw new Error('Failed to send message');
      }
    }

    showFeedback('success', `Message sent to ${resource.name}`);
  };

  const executeCommand = async (raw: string) => {
    const action = parseCommandAction(raw);
    if (!action || !token) return;

    setRunning(true);
    let opensAssignedList = false;
    try {
      switch (action.type) {
        case 'LA':
          await runLastAssigned(action.resource!);
          break;
        case 'AL':
          await runAssignedList(action.resource!);
          opensAssignedList = true;
          break;
        case 'ZTR':
          await runZoomToResource(action.resource!);
          break;
        case 'OI':
          runOpenIncident(action.incidentRef!);
          break;
        case 'MSG':
          await runSendMessage(action.resource!, action.messageText!);
          break;
        case 'STATUS':
          await runSetEventStatus(action.status!, action.statusReason || '');
          break;
      }
      onSearchChange('');
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Command failed');
    } finally {
      setRunning(false);
      const navigatesAway = action.type === 'LA' || action.type === 'OI';
      if (!navigatesAway && !opensAssignedList) {
        focusInput();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const value = searchQuery.trim();
    if (!value) return;

    if (looksLikeCommandAction(value)) {
      e.preventDefault();
      void executeCommand(value);
    }
  };

  return (
    <>
      <div className={cn('flex flex-col gap-1', className)}>
        <div className="relative flex-1" ref={helpRef}>
          <Terminal className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <div className="absolute right-1 top-1/2 -translate-y-1/2 z-10">
            {running ? (
              <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" />
            ) : (
              <button
                type="button"
                onClick={() => setShowHelp((v) => !v)}
                className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Show action line short codes"
                title="Short codes help"
              >
                <HelpCircle className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Action Line"
            className={cn(
              'w-full h-7 pl-7 pr-7 text-xs bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono',
              inputClassName
            )}
          />
          {showHelp && (
            <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-lg border border-border bg-card shadow-lg p-3 text-left">
              <p className="text-[10px] font-semibold text-foreground uppercase tracking-wide mb-2">
                Action line short codes
              </p>
              <ul className="space-y-2">
                {ACTION_BAR_SHORT_CODES.map((item) => (
                  <li key={item.code}>
                    <p className="text-xs font-mono font-medium text-primary">{item.code}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{item.description}</p>
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border">
                Press Enter to run a short code. Plain text filters the incident list.
              </p>
            </div>
          )}
        </div>
        {feedback && (
          <p
            className={cn(
              'text-[10px] px-1 leading-snug',
              feedback.type === 'error' && 'text-destructive',
              feedback.type === 'success' && 'text-emerald-500',
              feedback.type === 'info' && 'text-muted-foreground'
            )}
          >
            {feedback.message}
          </p>
        )}
      </div>

      <ResourceAssignedListModal
        open={alModal !== null}
        resourceLabel={alModal?.resourceLabel || ''}
        incidents={alModal?.incidents || []}
        onClose={() => {
          setAlModal(null);
          focusInput();
        }}
        onOpenIncident={(id) => {
          setAlModal(null);
          onOpenIncident(id);
        }}
      />
    </>
  );
}
